import { useState, useEffect } from 'react';
import { Table, Input, Row, Col, Spinner } from 'reactstrap';
import { Edit, RefreshCw } from 'react-feather';
import { toast } from 'react-toastify';
import { Btn } from '../../../AbstractElements';
import PermissionService from '../../../Api/services/permission.service';
import type { components } from '../../../Api/types/openapi';
import PermissionFormModal from './PermissionFormModal';
import { useTranslation } from 'react-i18next';

type Permission = components['schemas']['Permission'];
type PaginationPermission = components['schemas']['Pagination_Permission_'];

const PermissionsList = () => {
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [pagination, setPagination] = useState<PaginationPermission | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);

  useEffect(() => {
    loadPermissions();
  }, [currentPage, searchTerm]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit,
      };

      if (searchTerm) {
        params.name = searchTerm;
      }

      const data = await PermissionService.getPermissionsPaginated(params);
      
      if (!data || !Array.isArray(data.results)) {
        console.warn('Respuesta inválida del servidor: results no es un array');
        setPermissions([]);
        setPagination(null);
        return;
      }
      
      setPermissions(data.results);
      setPagination(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar permisos');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (permission: Permission) => {
    setSelectedPermission(permission);
    setShowModal(true);
  };

  const handleToggleState = async (permission: Permission) => {
    if (!permission.id) return;

    try {
      await PermissionService.changePermissionState(permission.id);
      toast.success(t('Estado del permiso actualizado'));
      loadPermissions();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cambiar estado');
      toast.error(errorMessage);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md="6">
          <Input
            type="text"
            placeholder={t('Buscar por nombre...')}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Col>
        <Col md="6" className="text-end">
          <Btn color="secondary" onClick={loadPermissions}>
            <RefreshCw size={16} className="me-1" />
            {t('Actualizar')}
          </Btn>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <>
          <Table responsive striped>
            <thead>
              <tr>
                <th>{t('ID')}</th>
                <th>{t('Nombre')}</th>
                <th>{t('Código')}</th>
                <th>{t('Estado')}</th>
                <th>{t('Acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {permissions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center">
                    {t('No se encontraron permisos')}
                  </td>
                </tr>
              ) : (
                permissions.map((permission) => (
                  <tr key={permission.id}>
                    <td>{permission.id}</td>
                    <td>{permission.name}</td>
                    <td>
                      <code>{permission.code || '-'}</code>
                    </td>
                    <td>
                      <div className="form-check form-switch">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          checked={permission.active}
                          onChange={() => handleToggleState(permission)}
                        />
                      </div>
                    </td>
                    <td>
                      <Btn
                        color="primary"
                        size="sm"
                        className="btn_list_action"
                        onClick={() => handleEdit(permission)}
                      >
                        <Edit size={14} />
                      </Btn>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {pagination && pagination.pages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                {t('Página')} {pagination.current_page} {t('de')} {pagination.pages}
              </div>
              <div>
                <Btn
                  color="primary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="me-2"
                >
                  {t('Anterior')}
                </Btn>
                <Btn
                  color="primary"
                  size="sm"
                  disabled={currentPage === pagination.pages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {t('Siguiente')}
                </Btn>
              </div>
            </div>
          )}
        </>
      )}

      <PermissionFormModal
        isOpen={showModal}
        toggle={() => setShowModal(false)}
        permission={selectedPermission}
        onSuccess={loadPermissions}
      />
    </div>
  );
};

export default PermissionsList;

